import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { gql } from '../graphql/generated';
import { UIActions } from '../ui/uiSlice';
import getReduxStore from './redux/getReduxStore';
import useAppSelector from './redux/useAppSelector';
import type { ValidationErrorHandler } from './apollo/getErrorLink';

const CURRENT_VERSION_QUERY = gql(`
  query currentVersion {
    currentVersion {
      version
    }
  }
`);

const checkVersion = async (
  client: ApolloClient<object>,
): ReturnType<ValidationErrorHandler> => {
  const store = getReduxStore();
  if (store.getState().ui.isVersionMismatchModalOpen) {
    return {
      isVersionMismatchModalOpen: true,
    };
  }

  const {
    data: {
      currentVersion: { version },
    },
  } = await client.query({
    query: CURRENT_VERSION_QUERY,
    fetchPolicy: 'network-only',
  });

  const [serverMajor, serverMinor] = version.split('.');
  const [clientMajor, clientMinor] = window.__slab.VERSION.split('.');

  if (serverMinor !== clientMinor || serverMajor !== clientMajor) {
    store.dispatch(UIActions.openVersionMismatchModal());
    return {
      isVersionMismatchModalOpen: true,
    };
  }

  return {
    isVersionMismatchModalOpen: false,
  };
};

export function useVersionCheck() {
  const socket = useAppSelector((state) => state.session.socket);
  const client = useApolloClient();
  const prevSocketRef = useRef<typeof socket>();

  useEffect(() => {
    if (socket) {
      socket.onOpen(() => {
        checkVersion(client);
      });
    }

    if (prevSocketRef.current == null && socket != null) {
      checkVersion(client);
    }

    prevSocketRef.current = socket;
  }, [client, socket]);
}

export default checkVersion;
